// Firebase init for the Ionic app.
//
// HIPAA / BAA scope: the app uses ONLY services covered by Google's Cloud BAA:
//   • Cloud Firestore (data)
//   • Firebase Authentication (sign-in)
// Realtime Database has been removed — it isn't on the HIPAA-covered Firebase
// list. Firebase Analytics has been removed for the same reason.
//
// Data model in Firestore (mirrors the previous RTDB layout 1:1 so legacy
// document IDs continue to work after the backfill):
//   /users/{uid}                — { private: { email, refresh_token,
//                                              ctm: { accounts, queues, key,
//                                                     receiving_numbers } },
//                                   restricted: { role, operatorRole, title },
//                                   salesforce_code }
//   /receiving_numbers/{number} — { ownerId: string | null }
//   /feedback/{id}              — FeedbackRecord (see interface below)
//   plus the PM collections owned by src/pm/firebase.ts.

import { initializeApp } from 'firebase/app';

import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';

import {
  Firestore,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

const config = {
  apiKey: 'AIzaSyBmpBdO2Oy0GMx8Ya5MqcPBEP_pOQ_6628',
  authDomain: 'rollover-rep-307a0.firebaseapp.com',
  projectId: 'rollover-rep-307a0',
  storageBucket: 'rollover-rep-307a0.appspot.com',
  messagingSenderId: '37686270375',
  appId: '1:37686270375:web:554b05b942f129701c3e82',
  // measurementId intentionally omitted — Firebase Analytics is not BAA-
  // covered and is no longer initialized.
};

const app = initializeApp(config);
const auth = getAuth();
// In-memory cache only (Firestore default). Persistent IndexedDB cache was
// considered but intentionally NOT enabled: it would write PHI-relevant data
// to the user's device at rest, adding endpoint-management requirements
// (full-disk encryption, MDM, idle timeout, cache wipe on logout) that we're
// not taking on right now. Trade-off: no offline reads, slightly slower cold
// loads.
const firestore: Firestore = getFirestore(app);

// Legacy export. Some callers reference `user` at import time; we keep the
// symbol so existing imports continue to compile.
var user = auth.currentUser;

// ---------- Users ----------

function userRef(userId: string) {
  return doc(firestore, 'users', userId);
}

// Set a nested map field at users/{uid}.private.ctm.<sub> to the given map.
// Replaces the previous value (matches the RTDB semantics where assigning
// an object at a path overwrote anything underneath).
async function writeUserCtmMap(
  userId: string,
  sub: 'accounts' | 'queues',
  map: Record<string, true>,
): Promise<void> {
  await setDoc(
    userRef(userId),
    { private: { ctm: { [sub]: map } } },
    { merge: true },
  );
}

export function clearUserCTMAccounts(userId: string): Promise<void> {
  return writeUserCtmMap(userId, 'accounts', {});
}

export function writeUserCTMAccounts(userId: string, accounts: any[]): Promise<void> {
  const map: Record<string, true> = {};
  for (const a of accounts) map[a.id] = true;
  return writeUserCtmMap(userId, 'accounts', map);
}

export function clearUserCTMQueues(userId: string): Promise<void> {
  return writeUserCtmMap(userId, 'queues', {});
}

export function writeUserCTMQueues(userId: string, queues: any[]): Promise<void> {
  const map: Record<string, true> = {};
  for (const q of queues) map[q.id] = true;
  return writeUserCtmMap(userId, 'queues', map);
}

export async function writeUserCTMKey(userId: string, key: string): Promise<void> {
  await setDoc(userRef(userId), { private: { ctm: { key } } }, { merge: true });
}

export async function writeBasicUserInfo(userId: string, email: string): Promise<void> {
  await setDoc(userRef(userId), { private: { email } }, { merge: true });
}

// Write the profile captured at sign-up. `facility` is the free-text treatment-
// facility string the user typed — it's the reconciliation hint the operator
// "Users" surface later uses to attach them to a customer/team workspace (see
// the auth handoff spec). Stored under the self-writable `public` map + a
// top-level `facility` field; never touches the operator-gated `restricted`.
export async function writeSignupProfile(
  userId: string,
  info: { email: string; firstName?: string; lastName?: string; facility?: string },
): Promise<void> {
  await setDoc(
    userRef(userId),
    {
      private: { email: info.email },
      public: {
        firstName: info.firstName || '',
        lastName: info.lastName || '',
      },
      facility: info.facility || '',
    },
    { merge: true },
  );
}

// One-shot read of a user doc. Resolves with the data, or with `null` when
// the doc doesn't exist — callers already guard with `if (data && data.x)`.
// Returning null avoids "Uncaught (in promise)" errors at app boot when the
// signed-in user has no profile doc yet (e.g. before the migration backfill,
// or for a brand-new account).
export function readUser(userId: string): Promise<any> {
  return getDoc(userRef(userId)).then((snap) => (snap.exists() ? snap.data() : null));
}

// Resolves with { [uid]: userData } when a match exists, or `null` when no
// user has that email. (Was previously a rejection, but callers don't have
// .catch() handlers — and "no such email" isn't an exceptional condition.)
export function getUserByEmail(email: string): Promise<Record<string, any> | null> {
  const q = query(collection(firestore, 'users'), where('private.email', '==', email));
  return getDocs(q).then((snap) => {
    if (snap.empty) return null;
    const result: Record<string, any> = {};
    snap.forEach((d) => {
      result[d.id] = d.data();
    });
    return result;
  });
}

// Resolves with the code, or '' when absent. Empty is the right "no code"
// sentinel for the callers, and avoids unhandled rejections at boot.
export function readUserSalesforceCode(userId: string): Promise<string> {
  return getDoc(userRef(userId)).then((snap) => {
    const data = snap.data() as any;
    return data && data.salesforce_code ? (data.salesforce_code as string) : '';
  });
}

export async function writeUserSalesforceCode(userId: string, code: string): Promise<void> {
  await setDoc(userRef(userId), { salesforce_code: code }, { merge: true });
}

// ---------- Refresh tokens ----------

export function generateSecureToken(length: number = 32): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < length; i++) {
    token += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return token;
}

export function validateRefreshToken(userId: string, refreshToken: string): Promise<any> {
  return new Promise((resolve, reject) => {
    readUser(userId)
      .then(async (u: any) => {
        if (u && u.private && u.private.refresh_token && u.private.refresh_token === refreshToken) {
          // u is a plain data object from Firestore; the original returned an
          // accessToken via auth's currentUser.getIdToken(). Preserve that
          // semantic by pulling the token from the currently signed-in user.
          const current = auth.currentUser;
          if (!current) {
            resolve(false);
            return;
          }
          const accessToken = await current.getIdToken();
          resolve(accessToken);
        } else {
          resolve(false);
        }
      })
      .catch(reject);
  });
}

export async function createRefreshToken(userId: string): Promise<string> {
  const refreshToken = generateSecureToken();
  await setDoc(userRef(userId), { private: { refresh_token: refreshToken } }, { merge: true });
  return refreshToken;
}

// ---------- Receiving numbers ----------

function rnRef(receivingNumber: string) {
  return doc(firestore, 'receiving_numbers', receivingNumber);
}

// Returns numbers either unclaimed or owned by `userId`. Resolves with an
// empty array when no numbers exist (the natural "nothing available" answer).
export function readAvailableReceivingNumbers(userId: string): Promise<string[]> {
  return getDocs(collection(firestore, 'receiving_numbers')).then((snap) => {
    const numbers: string[] = [];
    snap.forEach((d) => {
      const ownerId = (d.data() as any).ownerId ?? null;
      if (!ownerId || ownerId === userId) numbers.push(d.id);
    });
    return numbers;
  });
}

// Returns every receiving number annotated with its owner's email (when
// claimed). Mirrors the legacy admin view shape. Resolves with [] when the
// collection is empty.
export async function readAllReceivingNumbers(): Promise<any[]> {
  const snap = await getDocs(collection(firestore, 'receiving_numbers'));
  const numbers: any[] = [];
  for (const d of snap.docs) {
    const ownerId = (d.data() as any).ownerId ?? null;
    const obj: any = { number: d.id };
    if (ownerId) {
      obj.ownerId = ownerId;
      const u: any = await readUser(ownerId);
      if (u && u.private && u.private.email) obj.email = u.private.email;
    }
    numbers.push(obj);
  }
  return numbers;
}

// Update both the receiving-number doc and the owner's pointer atomically.
export async function updateReceivingNumber(
  receivingNumber: string,
  isClaiming: boolean,
  ownerId?: string,
): Promise<void> {
  const batch = writeBatch(firestore);
  if (isClaiming) {
    batch.set(rnRef(receivingNumber), { ownerId: ownerId ?? null }, { merge: true });
    if (ownerId) {
      batch.set(
        userRef(ownerId),
        { private: { ctm: { receiving_numbers: { [receivingNumber]: true } } } },
        { merge: true },
      );
    }
  } else if (ownerId) {
    batch.set(rnRef(receivingNumber), { ownerId: null }, { merge: true });
    batch.update(userRef(ownerId), {
      [`private.ctm.receiving_numbers.${receivingNumber}`]: false,
    });
  } else {
    // Admin "register a new number" path — no owner yet.
    batch.set(rnRef(receivingNumber), { ownerId: null }, { merge: true });
  }
  await batch.commit();
}

// Resolves with the ownerId string (or '' for unclaimed/absent). Empty is
// the natural "no owner" sentinel and avoids unhandled rejections.
export function readReceivingNumber(receivingNumber: string): Promise<string> {
  return getDoc(rnRef(receivingNumber)).then((snap) => {
    if (!snap.exists()) return '';
    const ownerId = (snap.data() as any).ownerId ?? null;
    return ownerId || '';
  });
}

export async function adminCreateReceivingNumber(receivingNumber: string): Promise<void> {
  await setDoc(rnRef(receivingNumber), { ownerId: null }, { merge: true });
}

export function deleteReceivingNumber(receivingNumber: string, ownerId?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    deleteDoc(rnRef(receivingNumber))
      .then(async () => {
        if (ownerId) {
          try {
            await updateDoc(userRef(ownerId), {
              [`private.ctm.receiving_numbers.${receivingNumber}`]: deleteField(),
            });
          } catch (err) {
            reject(err);
            return;
          }
        }
        resolve(`Receiving Number Deleted: ${receivingNumber}`);
      })
      .catch(reject);
  });
}

// ---------- Auth helpers ----------

export function getCurrentUser() {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, (u: any) => {
      if (u) resolve(u);
      else reject(null);
    });
    unsubscribe();
  });
}

export async function signInUserWithEmail(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password)
    .then((cred) => cred.user)
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error.code, error.message);
    });
}

export function logoutUser() {
  return signOut(auth)
    .then(() => {
      window.history.replaceState({}, '', '/Login');
      window.location.href = '/Login';
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
    });
}

// ---------- Feedback ----------

/**
 * Feedback record stored at /feedback/{id}. Shape is shared with the admin
 * inbox view + the Firestore security rules — keep the three in sync.
 *
 * status lifecycle: open → in_progress → closed. Default is 'open'; only
 * operators can transition (write rule gates non-creator writes).
 */
export interface FeedbackRecord {
  id: string;
  createdAt: number;
  userId: string;
  userEmail: string;
  message: string;
  context: string;
  status: 'open' | 'in_progress' | 'closed';
  assignedTo?: string | null;
  notes?: string;
  resolvedAt?: number | null;
}

export async function sendFeedback(message: string, context?: string): Promise<string> {
  const u = auth.currentUser;
  if (!u) throw new Error('You must be signed in to submit feedback.');
  // Reserve the id client-side so we can return it deterministically.
  const ref = doc(collection(firestore, 'feedback'));
  await setDoc(ref, {
    id: ref.id,
    createdAt: Date.now(),
    userId: u.uid,
    userEmail: u.email || '',
    message,
    context: context || '',
    status: 'open',
  });
  return ref.id;
}

export function listFeedback(
  onUpdate: (records: FeedbackRecord[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    collection(firestore, 'feedback'),
    (snap) => {
      const records: FeedbackRecord[] = [];
      snap.forEach((d) => {
        const r = d.data() as any;
        records.push({
          id: d.id,
          createdAt: typeof r.createdAt === 'number' ? r.createdAt : 0,
          userId: r.userId || '',
          userEmail: r.userEmail || '',
          message: r.message || '',
          context: r.context || '',
          status: (r.status as FeedbackRecord['status']) || 'open',
          assignedTo: r.assignedTo ?? null,
          notes: r.notes || '',
          resolvedAt: r.resolvedAt ?? null,
        });
      });
      records.sort((a, b) => b.createdAt - a.createdAt);
      onUpdate(records);
    },
    (err) => {
      // eslint-disable-next-line no-console
      console.error('listFeedback error:', err);
      if (onError) onError(err as unknown as Error);
    },
  );
}

export async function setFeedbackStatus(
  id: string,
  status: FeedbackRecord['status'],
): Promise<void> {
  const patch: Record<string, any> = { status };
  patch.resolvedAt = status === 'closed' ? Date.now() : null;
  await updateDoc(doc(firestore, 'feedback', id), patch);
}

export async function setFeedbackAssignee(
  id: string,
  assigneeUid: string | null,
): Promise<void> {
  await updateDoc(doc(firestore, 'feedback', id), { assignedTo: assigneeUid });
}

export { app, auth, user, firestore };
